import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { Droppable, Draggable } from 'react-beautiful-dnd';

import { ReleaseListHeaderCell, TableRow, TableSeparator } from 'components';
import { Creators as TableCreators } from 'reducers/Table';

import ddtStyles from './DragDropTable.module.scss';

const mapStateToProps = ({ releases, playQueue, global, table }) => ({
	sortedField: releases.sort,
	playingItem: playQueue.selectedItem,
	isIpadView: global.isIpadView,
	tableWidths: table
});


@connect(mapStateToProps)
class DragDropTable extends React.PureComponent {
	static propTypes = {
		items: PropTypes.arrayOf(PropTypes.object),
		columns: PropTypes.arrayOf(PropTypes.object),
		renderModal: PropTypes.func,
		onPlay: PropTypes.func,
		onClick: PropTypes.func,
		t: PropTypes.func,
		playingItem: PropTypes.object,
		isIpadView: PropTypes.bool,
		onHoverStop: PropTypes.func,
		controlledHover: PropTypes.bool,
		tableWidths: PropTypes.object,
		dispatch: PropTypes.func.isRequired,
		tableId: PropTypes.string.isRequired
	}
	static defaultProps = {
		controlledHover: false,
	};

	state = {
		sortedField: null,
		selectedItem: null
	}

	componentDidMount() {
		this.isMovingBarrier = false;
		this.movingBarrierField = -1;
		this.lastPosition = 0;
		this.setState({
			sortedItems: null,
			sortedField: null,
			sortedOrder: 'asc'
		});
	}

	componentDidUpdate(prevProps) {
		const { items: newItems } = this.props;
		const { items: prevItems } = prevProps;

		if (newItems !== prevItems) {
			/* eslint-disable react/no-did-update-set-state */
			// Refresh the sorted item list
			const { sortedField, sortedOrder } = this.state;

			if (!sortedField) {
				// Setting state improperly to skip an unneeded render
				this.state.sortedItems = newItems;
			} else {
				const sortedItems = [...newItems].sort(
					this.compareValues(sortedField, sortedOrder)
				);
				this.setState({ sortedItems });
			}
			/* eslint-enable react/no-did-update-set-state */
		}
	}

	componentWillUnmount() {
		if (this.wrapperRef) {
			this.wrapperRef.removeEventListener('scroll', this.handleWrapperScroll);
		}
		if (this.psRef) {
			this.psRef.removeEventListener('scroll', this.handlePseudoScroll);
		}
	}

	setWrapperRef = (ref) => {
		this.wrapperRef = ref;

		if (this.wrapperRef) {
			this.wrapperRef.addEventListener('scroll', this.handleWrapperScroll);
		}
	}

	setPseudoScrollRef = (ref) => {
		this.psRef = ref;

		if (this.psRef) {
			this.psRef.addEventListener('scroll', this.handlePseudoScroll);
		}
	}

	handleWrapperScroll = ({ target }) => {
		if (!this.psRef) return;

		this.psRef.scrollLeft = target.scrollLeft;
	}

	handlePseudoScroll = ({ target }) => {
		if (!this.wrapperRef) return;

		this.wrapperRef.scrollLeft = target.scrollLeft;
	}

	handleSortBy = newSort => {
		const { sortedField, sortedOrder } = this.state;
		const { items } = this.props;
		let newSortedOrder = 'asc';
		if (sortedField === newSort) {
			if (sortedOrder === 'asc') {
				newSortedOrder = 'desc';
			} else {
				newSortedOrder = 'asc';
			}
		}
		const sortedItems = [...items].sort(
			this.compareValues(newSort, newSortedOrder)
		);
		this.setState({
			sortedField: newSort,
			sortedItems,
			sortedOrder: newSortedOrder
		});
	}

	handleMouseMove = event => {
		const { isMovingBarrier, movingBarrierField, lastPosition } = this;
		const { dispatch, tableWidths, tableId } = this.props;
		if (isMovingBarrier) {
			const lastWidth = tableWidths[tableId][movingBarrierField];
			const x = event.clientX;
			this.lastPosition = x;
			const newWidth = lastWidth + x - lastPosition;
			dispatch(TableCreators.updateColumnWidth(tableId, movingBarrierField, newWidth));
		}
	}

	handleMouseUp = () => {
		setTimeout(() => {
			this.isMovingBarrier = false;
		}, 50);
		// This delay is added in order to prevent the onclick on the field to be mistriggered in dragging.
	}

	handleMouseDownSeparator = (event, field, init) => {
		const { tableWidths, tableId, dispatch } = this.props;
		const value = tableWidths[tableId][field];
		if (!value) {
			dispatch(TableCreators.updateColumnWidth(tableId, field, init));
		}
		this.isMovingBarrier = true;
		this.lastPosition = event.clientX;
		this.movingBarrierField = field;
	}

	handleClickOnField = field => {
		if (field) {
			const { isMovingBarrier } = this;
			if (!isMovingBarrier) {
				this.handleSortBy(field);
			}
		}
	}

	getScrollXState = () => {
		if (!this.wrapperRef) return false;

		const { offsetWidth, scrollWidth } = this.wrapperRef;

		// Show additional scroll only if container is overflowing on X.
		return offsetWidth < scrollWidth;
	}

	setSelectedItem = (item) => this.setState({ selectedItem: item });

	compareValues = (key, order = 'asc') => {
		return (a, b) => {
			if (
				!Object.prototype.hasOwnProperty.call(a, key) ||
				!Object.prototype.hasOwnProperty.call(b, key)
			) {
				return 0;
			}
			const varA =
				typeof a[key] === 'string' ? a[key].toUpperCase() : a[key];
			const varB =
				typeof b[key] === 'string' ? b[key].toUpperCase() : b[key];
			let comparison = 0;
			if (varA > varB) {
				comparison = 1;
			} else if (varA < varB) {
				comparison = -1;
			}
			return order === 'desc' ? comparison * -1 : comparison;
		};
	}

	renderBody = () => {
		const {
			items,
			columns,
			isIpadView,
			playingItem,
			controlledHover,
			t,
			renderModal,
			onPlay,
			onClick,
			onHoverStop,
			tableWidths,
			tableId
		} = this.props;
		const { sortedField, sortedItems, selectedItem } = this.state;

		const bodyCells = (sortedField ? sortedItems : items).map((item, index) => {
			let isHighlighted = false;
			if (item && playingItem) {
				if (item.trackid) {
					if (item.trackid === playingItem.trackid) {
						isHighlighted = true;
					}
				} else if (item.releaseid === playingItem.releaseid) {
					isHighlighted = true;
				}
			}
			return (
				<Draggable key={item.id} draggableId={item.id.toString()} index={index}>
					{(provided, snapshot) => (
						<div
							ref={provided.innerRef}
							{...provided.draggableProps}
							{...provided.dragHandleProps}>
							<TableRow
								isIpadView={isIpadView}
								isHighlighted={isHighlighted}
								controlledHover={controlledHover}
								columns={columns}
                item={item}
                tableId="dragdrop"
								// resizableWidths={tableWidths[tableId]}
								renderModal={renderModal}
								selectedItem={selectedItem}
								setSelectedItem={this.setSelectedItem}
								onPlay={onPlay}
								onClick={onClick}
								onHoverStop={onHoverStop} />
						</div>
					)}
				</Draggable>
			);
		});
		if (items.length === 0) {
			return (
				<div className="flex flex-col items-center mt-40 text-white font-header mx-auto">
					{t('release-list.noresults')}
					<Link push="true" to="/releases">
						<div className="text-white font-header bg-lightNavy px-12 py-2 mt-4 rounded-full shadow-button">
							{t('search.view-releases')}
						</div>
					</Link>
				</div>
			);
		} else {
			return bodyCells;
		}
	}

	renderHeader = () => {
		const { columns, renderModal, tableWidths, tableId } = this.props;
		const { sortedField, sortedOrder } = this.state;
		const releaseHeaderCells = columns.map((item, index) => {
			const resizableWidth = tableWidths[tableId][item.field];
			const width = resizableWidth || item.width;
			// if (item.image) {
			// 	return '';
			// } else {
				return (
					<div
						key={`${item.title} ${index + 1}`}
						className={`flex flex-row justify-center items-center ${
							item.cellClassName
            } ${item.desktopOnly ? 'xl:flex hidden' : ''} ${item.cellStyleName ? ddtStyles[item.cellStyleName] || '' : ''}`}
						style={{ width, useDrag: 'none' }}
						onClick={() => {
							this.handleClickOnField(item.sort);
						}}>
						<ReleaseListHeaderCell
							name={item.title}
							clickable={item.sort}
							sortedField={sortedOrder === 'asc' && sortedField} />
						{index !== columns.length - 1 ? (
							<React.Fragment>
								{/* <TableSeparator
									handleMouseDown={
										item.resizable &&
										(event => {
											event.stopPropagation();
											this.handleMouseDownSeparator(
												event,
												item.field,
												width
											);
										})
									} /> */}
							</React.Fragment>
						) : (
							null
						)}
					</div>
				);
			// }
		});
		return (
			<div className="relative w-full my-4">
				<div
					className={`flex flex-row items-center justify-start w-full ${ddtStyles['header-container']}`}
					onMouseMove={this.handleMouseMove}
					onMouseUp={this.handleMouseUp}
					onMouseLeave={this.handleMouseUp}>
					{/* <div styleName="release-index" className="h-12" />
					<div styleName="release-coverart" className="h-12" /> */}
					{releaseHeaderCells}
					{renderModal && <div className="w-12" />}
				</div>
			</div>
		);
	}

	renderTopScroll = () => {
		if (!this.wrapperRef) return null;

		const innerWidth = this.wrapperRef.scrollWidth;

		return (
			<div ref={this.setPseudoScrollRef} className="overflow-x-scroll scrollbar">
				<div style={{ width: innerWidth }} className="h-px" />
			</div>
		);
	}

	render() {
		const showXScroll = this.getScrollXState();

		return (
			<React.Fragment>
				{/* {
					showXScroll &&
					this.renderTopScroll()
				} */}
				<div ref={this.setWrapperRef} className="scrollbar">
					{ this.renderHeader() }

					<Droppable droppableId="droppable">
						{(provided) => (
							<div
								ref={provided.innerRef}
								{...provided.droppableProps}>
								{this.renderBody()}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</div>
			</React.Fragment>
		);
	}
}

export default withTranslation()(DragDropTable);
